// client/src/components/IconUpload.js
import React, { useState, useEffect } from 'react';

async function fetchJSON(url, opts = {}) {
  const res = await fetch(url, { credentials: 'include', ...opts });
  let body = null;
  try {
    body = await res.json();
  } catch {}
  if (!res.ok) {
    const msg = body?.error || body?.message || `${res.status} ${res.statusText}`;
    const e = new Error(msg);
    e.status = res.status;
    e.body = body;
    throw e;
  }
  return body;
}

function cacheBust(url, seed) {
  if (!url) return url;
  const u = String(url);
  const sep = u.includes('?') ? '&' : '?';
  const t = typeof seed === 'number' ? seed : Date.now();
  return `${u}${sep}t=${t}`;
}

function announceTypeIconUpdated({ typeId, iconUrl }) {
  const ts = Date.now();
  try {
    window.__PA_ICON_BUST_TS__ = ts;
  } catch {}
  try {
    window.dispatchEvent(
      new CustomEvent('pa:type-icon-updated', { detail: { typeId, iconUrl, ts } })
    );
  } catch {}
}

export default function IconUpload({
  initialIconUrl = '',
  mapId,
  typeId,
  // shapeId is intentionally ignored here; Geoman uses generic mode via onIconUploaded
  shapeId,
  onIconUploaded,
  onCancel,
  disabled = false,
}) {
  const [existingIcons, setExistingIcons] = useState([]);
  const [iconsLoading, setIconsLoading] = useState(false);
  const [iconsError, setIconsError] = useState('');

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [selectedExistingId, setSelectedExistingId] = useState('');
  const [selectedUrl, setSelectedUrl] = useState(initialIconUrl || '');

  // Modes:
  // - Type mode: has typeId -> persist directly to poi_types via /api/type-edit/:typeId/*
  // - Generic mode (e.g., Geoman & POI editor): no typeId -> upload to map library and return URL
  const isTypeMode = !!typeId;
  const isGenericMode = !isTypeMode && typeof onIconUploaded === 'function';

  useEffect(() => {
    setSelectedUrl(initialIconUrl || '');
  }, [initialIconUrl]);

  useEffect(() => {
    if (!mapId) return;
    let cancelled = false;

    async function loadIcons() {
      setIconsLoading(true);
      setIconsError('');
      try {
        // Only use the map icon catalog from type-edit; polygons icon catalog endpoints are not used.
        const typeData = await fetchJSON(`/api/type-edit/map/${mapId}/icons`).catch(() => null);
        if (cancelled) return;

        const typeIcons = Array.isArray(typeData?.icons) ? typeData.icons : [];
        setExistingIcons(typeIcons);

        if (typeIcons.length > 0) {
          const match = typeIcons.find(i => i.icon_file === initialIconUrl);
          setSelectedExistingId(String((match ?? typeIcons[0]).id ?? (match ?? typeIcons[0]).icon_file));
        } else {
          setSelectedExistingId('');
        }

        if (!typeData) {
          setIconsError('Failed to load existing icons');
        }
      } catch (e) {
        if (!cancelled) {
          setIconsError(e.message || 'Failed to load existing icons');
          setExistingIcons([]);
          setSelectedExistingId('');
        }
      } finally {
        if (!cancelled) setIconsLoading(false);
      }
    }

    loadIcons();
    return () => {
      cancelled = true;
    };
  }, [mapId, initialIconUrl]);

  function handleFileChange(e) {
    setError('');
    setMessage('');
    const f = e.target.files?.[0] || null;
    if (!f) {
      setFile(null);
      return;
    }
    if (!/^image\/png$/i.test(f.type)) {
      setError('Only PNG files are allowed');
      setFile(null);
      return;
    }
    if (f.size > 256 * 1024) {
      setError('Max file size is 256KB');
      setFile(null);
      return;
    }
    setFile(f);
  }

  async function handleUpload() {
    if (disabled) return;
    if (!file) {
      setError('Choose a PNG file first.');
      return;
    }
    setUploading(true);
    setError('');
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('icon', file);
      if (mapId != null) formData.append('map_id', String(mapId));

      let iconUrl = '';

      if (isTypeMode) {
        // Persist directly to poi_types
        const res = await fetch(`/api/type-edit/${typeId}/icon`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Upload failed');
        iconUrl = data.icon_url;
        announceTypeIconUpdated({ typeId, iconUrl });
      } else if (isGenericMode) {
        // Upload into the map's icon library and return URL to caller
        if (!mapId) throw new Error('Missing mapId for upload');
        const data = await fetchJSON(`/api/type-edit/map/${mapId}/icon-upload`, {
          method: 'POST',
          body: formData,
        });
        iconUrl = data.icon_url;
      } else {
        throw new Error('No valid upload target configured');
      }

      if (!iconUrl) throw new Error('Upload did not return an icon URL.');

      const busted = cacheBust(iconUrl);
      setSelectedUrl(busted);
      setMessage('Icon uploaded successfully');
      setFile(null);

      // Ensure it appears in existing list
      setExistingIcons(prev => {
        if (prev.some(i => i.icon_file === iconUrl)) return prev;
        const name =
          file.name && typeof file.name === 'string'
            ? file.name.replace(/\.[^.]+$/, '')
            : 'Icon';
        return [...prev, { id: null, name, icon_file: iconUrl }];
      });

      // Notify caller for UI sync in all modes
      if (typeof onIconUploaded === 'function') {
        onIconUploaded(busted);
      }
    } catch (e) {
      setError(e.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  async function handleUseExisting() {
    setError('');
    setMessage('');

    const selected = existingIcons.find(
      i => String(i.id ?? i.icon_file) === String(selectedExistingId)
    );
    if (!selected || !selected.icon_file) {
      setError('Invalid icon selection');
      return;
    }

    const rawUrl = selected.icon_file;
    const busted = cacheBust(rawUrl);

    try {
      if (isTypeMode) {
        // Assign to poi_types row directly
        await fetchJSON(`/api/type-edit/${typeId}/icon-assign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ icon_url: rawUrl }),
        });
        announceTypeIconUpdated({ typeId, iconUrl: rawUrl });
        setMessage('Icon assigned from existing icons');
      } else if (isGenericMode) {
        // Return URL to caller for them to persist (e.g., Geoman shape PUT)
        setMessage('Icon selected');
      } else {
        throw new Error('No valid assign target configured');
      }

      setSelectedUrl(busted);

      if (typeof onIconUploaded === 'function') {
        onIconUploaded(busted);
      }
    } catch (e) {
      setError(e.message || 'Failed to assign existing icon');
    }
  }

  const selectedExisting = existingIcons.find(
    i => String(i.id ?? i.icon_file) === String(selectedExistingId)
  );

  return (
    <div
      style={{
        background: '#232427',
        borderRadius: 12,
        padding: 20,
        minWidth: 340,
        maxWidth: 420,
        boxShadow: '0 2px 18px #000c',
        color: '#e2e4e9',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          {isTypeMode ? 'Choose Type Icon' : 'Choose Icon'}
        </div>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              background: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '4px 9px',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div style={{ fontSize: 13, color: '#c9cbcf', marginBottom: 12 }}>
        You can reuse an existing icon from this map or upload a new PNG (max 256KB).
      </div>

      {initialIconUrl && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Current Icon:</div>
          <img
            src={cacheBust(initialIconUrl)}
            alt="Current Icon"
            style={{
              width: 40,
              height: 40,
              borderRadius: 6,
              border: '1px solid #444',
              background: '#111',
            }}
            onError={e => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      )}

      <div
        style={{
          border: '1px solid #3a3a3a',
          borderRadius: 8,
          padding: 10,
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Use Existing Icon</div>
        {iconsLoading ? (
          <div style={{ fontSize: 13, color: '#9aa0a6' }}>Loading icons…</div>
        ) : existingIcons.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9aa0a6' }}>
            No existing icons found for this map.
          </div>
        ) : (
          <>
            <select
              value={selectedExistingId}
              onChange={e => setSelectedExistingId(e.target.value)}
              style={{
                width: '100%',
                background: '#333',
                color: '#fff',
                border: '1px solid #444',
                borderRadius: 6,
                padding: '6px 10px',
                marginBottom: 8,
              }}
            >
              {existingIcons.map(icon => (
                <option
                  key={String(icon.id ?? icon.icon_file)}
                  value={String(icon.id ?? icon.icon_file)}
                >
                  {icon.name ? icon.name : 'Icon'}
                </option>
              ))}
            </select>
            {selectedExisting && selectedExisting.icon_file && (
              <div style={{ marginBottom: 8 }}>
                <img
                  src={cacheBust(selectedExisting.icon_file)}
                  alt="Selected Icon Preview"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 6,
                    border: '1px solid #444',
                    background: '#111',
                  }}
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
            <button
              onClick={handleUseExisting}
              disabled={!selectedExistingId || uploading || disabled}
              style={{
                background: '#4cc153',
                color: '#101314',
                border: 'none',
                borderRadius: 7,
                padding: '6px 14px',
                fontWeight: 700,
                fontSize: 14,
                cursor:
                  selectedExistingId && !uploading && !disabled
                    ? 'pointer'
                    : 'not-allowed',
              }}
            >
              Use Selected Icon
            </button>
          </>
        )}
        {iconsError && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#f44c2b' }}>{iconsError}</div>
        )}
      </div>

      <div
        style={{
          border: '1px solid #3a3a3a',
          borderRadius: 8,
          padding: 10,
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>Upload New Icon</div>
        <input type="file" accept="image/png" onChange={handleFileChange} disabled={uploading || disabled} />
        {file && (
          <div style={{ marginTop: 6, fontSize: 13 }}>
            Selected: {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <button
          onClick={handleUpload}
          disabled={!file || uploading || disabled}
          style={{
            marginTop: 8,
            background: '#4cc153',
            color: '#101314',
            border: 'none',
            borderRadius: 7,
            padding: '6px 14px',
            fontWeight: 700,
            fontSize: 14,
            cursor: file && !uploading && !disabled ? 'pointer' : 'not-allowed',
          }}
        >
          {uploading ? 'Uploading…' : 'Upload PNG'}
        </button>
      </div>

      {error && <div style={{ color: '#f44c2b', fontSize: 13, marginTop: 4 }}>{error}</div>}
      {message && <div style={{ color: '#81ff9c', fontSize: 13, marginTop: 4 }}>{message}</div>}

      <div style={{ marginTop: 10, textAlign: 'right' }}>
        {onCancel && (
          <button
            onClick={onCancel}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              padding: '6px 14px',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}
